import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import inputStyles from '../views/program-management/ProgramManagement.module.css';
import styles from './ManagementDropdownCard.module.css';
import { Form } from "react-bootstrap";
import { EnrollmentDropdowns } from './EnrollmentDropdown';
import { getModalityId } from '../utils/programUtils';

const ModalityMeasurementSelection = (props) => {
    const {
        deviceId,
        deviceModalityId,
        setModality,
        dataList,
        disabled,
        errors,
        allDevices
    } = props;

    const userUnitPreference = useSelector((state) => state.userInfo.unitType);
    const defaultModalities = useSelector((state) => state.dropdowns.modality.values);
    const dispatch = useDispatch();
    const modalityId = getModalityId(deviceModalityId, deviceId, allDevices);

    // Add default data object if no matching modality is found
    const defaultData = {
        deviceId,
        deviceModalityId,
        upperLimit: 0,
        lowerLimit: 0,
        targetValue: 0,
        isAlarmActive: false
    };

    const data = dataList.find((item) => (item.deviceId === deviceId && item.deviceModalityId === deviceModalityId)) || defaultData;

    // Initialize state with safe values
    const [thresholdToggled, setThresholdToggled] = useState(data.upperLimit !== 0 || data.lowerLimit !== 0);
    const [goalToggled, setGoalToggled] = useState(data.targetValue !== 0);
    const [localValues, setLocalValues] = useState({
        upperLimit: '',
        lowerLimit: '',
        targetValue: ''
    });

    // If the parent disables this modality (e.g. "Enabled" unchecked),
    // close the threshold/goal UI so it doesn't stay expanded with cleared values.
    useEffect(() => {
        if (disabled) {
            setThresholdToggled(false);
            setGoalToggled(false);
        }
    }, [disabled]);

    // Convert input value to metric for backend
    const toMetricValue = (displayValue) => {
        if (!displayValue || displayValue === 0) return 0;

        const numValue = Number(displayValue);

        // If user preference is Metric, just return the value as is, formatted to 1 decimal place
        if (userUnitPreference === 'Metric') return parseFloat(numValue.toFixed(1));

        let metricValue;
        switch (modalityId) {
            case 8: // Weight
                // Convert Imperial (lbs) to Metric (kg)
                metricValue = numValue * 0.45359237;
                return parseFloat(metricValue.toFixed(1));
            case 1: // Temperature
                // Convert Fahrenheit to Celsius for storage and format to 1 decimal place
                metricValue = (numValue - 32) * 5 / 9;
                return parseFloat(metricValue.toFixed(1));
            default:
                return Math.round(numValue);
        }
    };

    // Convert display value based on unit preference
    const getDisplayValue = (metricValue) => {
        if (metricValue === null || metricValue === undefined || metricValue === '' || metricValue === 0) return '';
        
        const numValue = Number(metricValue);
        if (isNaN(numValue)) return '';
        
        // If user preference is Metric, just return the value as is, formatted to 1 decimal place
        if (userUnitPreference === 'Metric') return parseFloat(numValue.toFixed(1));

        switch (modalityId) {
            case 8: // Weight
                const imperialWeight = Math.round(numValue * 2.20462262185);
                return imperialWeight;
            case 1: // Temperature
                // Convert Celsius to Fahrenheit for display only
                const imperialTemp = Math.round(numValue * 9 / 5 + 32);
                return imperialTemp;
            default:
                return Math.round(numValue);
        }
    };

    const limits = useMemo(() => {
        if (!modalityId) {
            return {
                maxHigh: '',
                maxLow: '',
                minHigh: '',
                minLow: '',
                defaultLow: '',
                defaultHigh: ''
            };
        }
        
        const defaultModValues = defaultModalities.find(item => item.modalityId === modalityId);
    
        if (defaultModValues) {
            return {
                defaultLow: defaultModValues.defaultLow ?? '',
                defaultHigh: defaultModValues.defaultHigh ?? '',
                maxHigh: getDisplayValue(defaultModValues.maximumHigh) ?? '',
                maxLow: getDisplayValue(defaultModValues.maximumLow) ?? '',
                minHigh: getDisplayValue(defaultModValues.minimumHigh) ?? '',
                minLow: getDisplayValue(defaultModValues.minimumLow) ?? ''
            };
        }
    
        return {
            maxHigh: '',
            maxLow: '',
            minHigh: '',
            minLow: '',
            defaultLow: '',
            defaultHigh: ''
        };
    }, [defaultModalities, modalityId, userUnitPreference]);

    // Update local values when data changes
    useEffect(() => {
        if (modalityId) {
            setLocalValues({
                upperLimit: getDisplayValue(data.upperLimit) || '',
                lowerLimit: getDisplayValue(data.lowerLimit) || '',
                targetValue: getDisplayValue(data.targetValue) || ''
            });
        }
    }, [userUnitPreference, modalityId, data.upperLimit, data.lowerLimit, data.targetValue]);

    // Early return if modalityId is not found
    if (!modalityId) {
        return <div>Loading modality data...</div>;
    }

    const showErrors = errors?.modalityErrors?.some(id => id === deviceModalityId) || false;

    const findUnit = () => {
        if (allDevices) {
            for (const device of allDevices) {
                if (device.modalities) {
                    const result = device.modalities.find(modality => modality.deviceModalityId === deviceModalityId);
                    if (result) {
                        const unit = userUnitPreference === "Metric" ? result.modalityUnitMetric : result.modalityUnitImperial;
                        return unit;
                    }
                }
            }
        }
        return null;
    };
    const unit = findUnit();

    const handleThresholdToggle = () => {
        const toggled = !thresholdToggled;
        if (!toggled) {
            // if user just toggled threshold off, set upperLimit and lowerLimit to 0
            dispatch(setModality({
                ...data,
                deviceId: deviceId,
                deviceModalityId: deviceModalityId,
                upperLimit: 0,
                lowerLimit: 0
            }));
        } 
        else {
            // user toggled thresholds on, populate with default values
            dispatch(setModality({
                ...data,
                deviceId: deviceId,
                deviceModalityId: deviceModalityId,
                upperLimit: limits.defaultHigh !== '' ? limits.defaultHigh : 0,
                lowerLimit: limits.defaultLow !== '' ? limits.defaultLow : 0
            }));
        }
        setThresholdToggled(toggled);
    };

    const handleGoalToggle = () => {
        const toggled = !goalToggled;
        if (!toggled) {
            // if user just toggled goal off, set targetGoal to 0
            dispatch(setModality({
                ...data,
                deviceId: deviceId,
                deviceModalityId: deviceModalityId,
                targetValue: 0,
            }))
        }
        setGoalToggled(toggled);
    };

    const handleInputChange = (field, value) => {
        // Update local state immediately for display
        setLocalValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBlur = (field) => {
        const value = localValues[field];
        // Only process if the value is a valid number (including decimals) or empty
        if (value === '' || /^\d*\.?\d+$/.test(value)) {
            // Convert input value to number immediately
            const numericValue = value === '' ? 0 : Number(value);

            // Use toMetricValue for all conversions
            const metricValue = toMetricValue(numericValue);
            dispatch(setModality({
                ...data,
                [field]: metricValue
            }));
        }
    };

    return (
        <>
        {(data.deviceId === 1 && EnrollmentDropdowns.disabledVitals.includes(data.modalityId)) ?
         <div style={{padding:"10px 20px", fontSize:"12px"}}>
         Threshold and goal settings are unavailable for this modality.
     </div> :
        <div style={{ display: "flex", flexDirection: "column", gap: "0", paddingLeft: "20px", paddingTop: "5px" }}>
            <div style={{ display: "flex", columnGap: "10px", alignItems: "center",height: "44px" }}>
                <div style={{ display: "flex", width: "110px", alignItems: "center" }}>
                    <Form>
                        <Form.Check
                            style={{ paddingBottom: "2px" }}
                            type="switch"
                            id="custom-switch"
                            disabled={disabled}
                            checked={thresholdToggled}
                            onChange={handleThresholdToggle}
                        />
                    </Form>
                    <span className={styles.ModalityMeasurementLabel}>Threshold </span>
                </div>
                {thresholdToggled &&
                    <div style={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
                        <div style={{ position: "relative" }}>
                            <span className={inputStyles.FloatingLabel}>Upper Limit</span>
                            <input className={inputStyles.InputStyling}
                                style={showErrors ? { border: "1px solid red", width: "140px", padding: "17px 12px 10px 10px" } : { width: "140px", padding: "17px 12px 10px 10px" }}
                                type="number"
                                id="upperLimit"
                                disabled={disabled}
                                value={localValues.upperLimit}
                                onChange={(e) => handleInputChange('upperLimit', e.target.value)}
                                onBlur={() => handleBlur('upperLimit')}
                            ></input>
                        </div>
                        <span className={styles.ModalityMeasurementLabel}>{unit}</span>
                        <div style={{ position: "relative" }}>
                            <span className={inputStyles.FloatingLabel}>Lower Limit</span>
                            <input className={inputStyles.InputStyling}
                                style={showErrors ? { border: "1px solid red", width: "140px", padding: "17px 12px 10px 10px" } : { width: "140px", padding: "17px 12px 10px 10px" }}
                                type="number"
                                id="lowerLimit"
                                disabled={disabled}
                                value={localValues.lowerLimit}
                                onChange={(e) => handleInputChange('lowerLimit', e.target.value)}
                                onBlur={() => handleBlur('lowerLimit')}
                            ></input>
                        </div>
                        <span className={styles.ModalityMeasurementLabel}>{unit}</span>
                    </div>
                }
            </div>
            {thresholdToggled && limits && (limits.maxHigh !== '' || limits.maxLow !== '' || limits.minHigh !== '' || limits.minLow !== '') &&
                <div style={{ display: "flex", columnGap: "10px", alignItems: "center", height:"19px" }}>
                    <div style={{ width: "110px", height:"19px", color:"white" }}></div>
                    {/* Upper Limits */}
                    <div style={{ width: "140px"}}>
                        {(limits.maxHigh !== '' || limits.maxLow !== '') && (
                            <>
                                <div style={{fontSize:"12px"}}>
                                    {limits.minHigh !== '' && `Min: ${limits.minHigh}`}
                                    {limits.maxHigh !== '' && ` Max: ${limits.maxHigh}`}
                                </div>
                            </>
                        )}
                    </div>
                    <span style={{color:'white'}} className={styles.ModalityMeasurementLabel}>{unit}</span>
                    <div style={{ width: "140px"}}>
                        {/* Lower Limits */}
                        {(limits.minHigh !== '' || limits.minLow !== '') && (
                            <>
                                <div style={{fontSize:"12px"}}>
                                    {limits.minLow !== '' && ` Min: ${limits.minLow}`}
                                    {limits.maxLow !== '' && ` Max: ${limits.maxLow}`}
                                </div>
                            </>
                        )}
                    </div>
                    <span style={{color:'white'}} className={styles.ModalityMeasurementLabel}>{unit}</span>
                </div>
            }
            <div style={{ display: "flex", columnGap: "10px", alignItems: "center", height: "44px", marginTop: "5px"}}>
                <div style={{ display: "flex", width: "110px", alignItems: "center" }}>
                    <Form>
                        <Form.Check
                            style={{ paddingBottom: "2px" }}
                            type="switch"
                            id="custom-switch"
                            disabled={disabled}
                            checked={goalToggled}
                            onChange={handleGoalToggle}
                        />
                    </Form>
                    <span className={styles.ModalityMeasurementLabel}>Goal</span>
                </div>
                {goalToggled &&
                    <div style={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
                        <div style={{ position: "relative" }}>
                            <span className={inputStyles.FloatingLabel}>Target Value</span>
                            <input className={inputStyles.InputStyling}
                                style={showErrors ? { border: "1px solid red", width: "140px", padding: "17px 12px 10px 10px" } : { width: "140px", padding: "17px 12px 10px 10px" }}
                                type="number"
                                id="targetValue"
                                disabled={disabled}
                                value={localValues.targetValue}
                                onChange={(e) => handleInputChange('targetValue', e.target.value)}
                                onBlur={() => handleBlur('targetValue')}
                            ></input>
                        </div>
                        <span className={styles.ModalityMeasurementLabel}>{unit}</span>
                    </div>
                }
            </div>
        </div>
        }
        </>
    )
}

export default ModalityMeasurementSelection;